import { defaultWebsiteId } from '@va/constants';
import {
  AGREEMENT_REQUEST,
  agreementFailed,
  agreementSucceeded,
  GET_AGREEMENT_REQUEST,
  GET_ANNOUNCEMENTS,
  GET_FEATURE_PERMISSIONS,
  GET_FEATURE_PERMISSIONS_FAILED,
  GET_FEATURE_PERMISSIONS_SUCCEEDED,
  GET_PRIVACY_SETTINGS,
  getAgreementFailed,
  getAgreementSucceeded,
  getAnnouncementsFailed,
  getAnnouncementsSucceeded,
  getFeaturePermissionsFailed,
  getFeaturePermissionsSucceeded,
  getPrivacySettingsFailed,
  getPrivacySettingsSucceeded,
  requestWebsiteFailed,
  requestWebsiteSucceeded,
  resetRequest,
  SIGN_DPA_REQUEST,
  signDPAFailed,
  signDPASucceeded,
  UPDATE_SETTINGS_FAILED,
  UPDATE_SETTINGS_SUCCEEDED,
  UPDATE_WEBSITE_FLAG,
  UPDATE_WEBSITE_FLAGS_REQUEST,
  updateSettings,
  updateWebsite,
  updateWebsiteFlags,
  updateWebsiteFlagsFailed,
  updateWebsiteFlagsSucceeded,
  WEBSITE_REQUEST,
} from '@va/dashboard/actions/api';
import { setGlobalFilter } from '@va/dashboard/actions/ui';
import Api from '@va/dashboard/api-client/index';
import { getFeaturePermissionsStatus, getInstanceId } from '@va/dashboard/selectors/app';
import {
  getWebsiteFrontendFlags,
  getWebsite as getWebsiteSelector,
  getWebsiteTimezone,
} from '@va/dashboard/selectors/core';
import { getLocale } from '@va/dashboard/selectors/ui';
import { activeWebsiteSlice } from '@va/dashboard/slices';
import { hasFreePlan as hasFreePlanSelector } from '@va/standalone/shared/selectors';
import {
  announcementsMapper,
  getFilterDateWithTimezone,
  getGlobalFilterFromUrl,
  getTimePeriodFromStorage,
  getTimezoneFromQueryParams,
  isValidWebsiteId,
  successOrFailedStatus,
} from '@va/util/helpers';
import { getPredefinedFilters } from '@va/wix-mini/shared/selectors';
import { PREDEFINED_PERIOD } from '@va/wix-mini/shared/types';
import Immutable from 'immutable';
import moment from 'moment';
import { all, call, delay, put, race, select, spawn, take, takeLatest } from 'redux-saga/effects';

export function* watchers() {
  yield all([
    takeLatest(WEBSITE_REQUEST, getWebsiteSaga),
    takeLatest(UPDATE_WEBSITE_FLAGS_REQUEST, updateWebsiteFlagsSaga),
    takeLatest(GET_AGREEMENT_REQUEST, getAgreement),
    takeLatest(AGREEMENT_REQUEST, agreement),
    takeLatest(SIGN_DPA_REQUEST, signAgreement),
    takeLatest(GET_FEATURE_PERMISSIONS, getFeaturePermissions),
    takeLatest(GET_ANNOUNCEMENTS, getAnnouncements),
    takeLatest(UPDATE_WEBSITE_FLAG, updateWebsiteFlagWatcher),
    takeLatest(GET_PRIVACY_SETTINGS, getPrivacySettingsWatcher),
  ]);
}

export function* updateWebsiteLocale(action) {
  try {
    const requestData = {
      locale: action.locale,
    };
    const websiteId = yield select(getInstanceId);
    yield call(Api.updateSettings, websiteId, requestData);
    yield call(getWebsiteSaga);
  } catch (error) {
    console.error('failed to update website locale', error);
  }
}

function* updateWebsiteFlagWatcher(action) {
  yield put(updateWebsiteFlags({ [action.key]: action.value }, action.optimisticUpdate));
}

/**
 * Tracks whether the global date filter has already been resolved once since the app was loaded.
 * The first resolution corresponds to a direct link open.
 */
let isInitialFilterResolution = true;

// Upper bound for waiting on feature permissions. Just in case feature permissions are reset, to avoid blocking the saga.
const FEATURE_PERMISSIONS_WAIT_TIMEOUT = 10000;

function* waitForFeaturePermissions() {
  const status = yield select(getFeaturePermissionsStatus);
  if (successOrFailedStatus(status)) return;

  yield race({
    settled: take([GET_FEATURE_PERMISSIONS_SUCCEEDED, GET_FEATURE_PERMISSIONS_FAILED]),
    timeout: delay(FEATURE_PERMISSIONS_WAIT_TIMEOUT),
  });
}

function* handleFilterAfterWebsiteSwitch(installDateFromFetch, timezone) {
  const isInitialOpen = isInitialFilterResolution;
  isInitialFilterResolution = false;

  const predefinedFilters = yield select(getPredefinedFilters);

  if (predefinedFilters?.predefinedTimePeriod) {
    yield spawn(setPredefinedTimePeriod);
    return;
  }

  // Free-plan status must be read only after the switch's feature-permissions fetch settles.
  yield call(waitForFeaturePermissions);

  const websiteTimezone = yield select(getWebsiteTimezone);
  const websiteId = yield select(getInstanceId);
  const hasFreePlan = yield select(hasFreePlanSelector);

  // 1. URL params (deep-link intent) — honored only on a direct/initial open, never on a website switch.
  if (isInitialOpen) {
    const urlFilter = getGlobalFilterFromUrl(websiteTimezone, installDateFromFetch);

    if (urlFilter) {
      // TO DO: Replace moment with dayjs
      const today = moment().tz(websiteTimezone, true).endOf('day');
      const { dateRangeKey } = urlFilter;
      let { from, until } = urlFilter;

      if (hasFreePlan && websiteId !== defaultWebsiteId) {
        const freePlanStartDate = moment().tz(websiteTimezone, true).subtract(39, 'days').startOf('day');

        if (until.isBefore(freePlanStartDate)) {
          // The whole range is outside the free plan history, so the whole available period is shown instead (same as the date picker's minimum date check)
          from = freePlanStartDate;
          until = today;
        } else {
          from = moment.max(from, freePlanStartDate);
        }
      }

      from = moment.min(from, today);
      until = moment.min(until, today);

      yield put(setGlobalFilter({ from, until, dateRangeKey }));
      return;
    }
  }

  // 2. Stored value for the (target) website.
  const storedFilter = getTimePeriodFromStorage(websiteId, websiteTimezone, installDateFromFetch, hasFreePlan);

  if (storedFilter) {
    const fromDate =
      storedFilter.from.unix() < installDateFromFetch
        ? moment(installDateFromFetch * 1000).startOf('day')
        : storedFilter.from;
    yield put(setGlobalFilter({ from: fromDate, until: storedFilter.until, dateRangeKey: storedFilter.dateRangeKey }));
    return;
  }

  // 3. Default — last 30 days, never earlier than the install date.
  const defaultFrom = getFilterDateWithTimezone(moment().subtract(29, 'days').startOf('day'), timezone);
  const fromDate =
    defaultFrom.unix() > installDateFromFetch ? defaultFrom : moment(installDateFromFetch * 1000).startOf('day');

  yield put(setGlobalFilter({ from: fromDate, until: moment().endOf('day') }));
}

function* setPredefinedTimePeriod() {
  const { predefinedTimePeriod, timezone } = yield select(getPredefinedFilters);

  if (!predefinedTimePeriod) return;

  let timeFilter = {
    from: undefined,
    until: moment().tz(timezone).endOf('day'),
  };

  switch (predefinedTimePeriod) {
    case PREDEFINED_PERIOD.TODAY:
      timeFilter.from = moment().tz(timezone).startOf('day');
      break;
    case PREDEFINED_PERIOD.YESTERDAY:
      timeFilter.from = moment().tz(timezone).subtract(1, 'day').startOf('day');
      timeFilter.until = moment().tz(timezone).subtract(1, 'day').endOf('day');
      break;
    case PREDEFINED_PERIOD.THIS_WEEK:
      timeFilter.from = moment().tz(timezone).startOf('week');
      break;
    case PREDEFINED_PERIOD.LAST_7_DAYS:
      timeFilter.from = moment().tz(timezone).subtract(6, 'day').startOf('day');
      break;
    case PREDEFINED_PERIOD.THIS_MONTH:
      timeFilter.from = moment().tz(timezone).startOf('month');
      break;
    case PREDEFINED_PERIOD.LAST_30_DAYS:
    default:
      timeFilter.from = moment().tz(timezone).subtract(29, 'day').startOf('day');
  }

  yield put(setGlobalFilter(timeFilter));
}

export function* getWebsiteSaga() {
  try {
    const websiteId = yield select(getInstanceId);
    const data = yield call(Api.getWebsite, websiteId);

    const presetTimezone = getTimezoneFromQueryParams();
    // If timezone is already in the query params, do not override. Used for wix mini
    if (!presetTimezone) {
      window.timezone = data.timezone;
      // TODO Maybe set momentJS and DayJS timezone here?
    }

    yield put(requestWebsiteSucceeded(data));
    yield put(activeWebsiteSlice.actions.setCoreData(data));

    yield spawn(handleFilterAfterWebsiteSwitch, data['installDate'], data['timezone']);
    return data; // return data so callers can retrieve the result directly via yield call()
  } catch (error) {
    yield put(requestWebsiteFailed(error));
  }
}

export function* updateWebsiteFlagsSaga(action) {
  const currentFrontendFlags = yield select(getWebsiteFrontendFlags);
  const websiteId = yield select(getInstanceId);

  const performOptimisticUpdate = action.optimisticUpdate;

  try {
    if (performOptimisticUpdate) {
      // Optimistically update the flags
      yield put(updateWebsite({ frontendFlags: { ...currentFrontendFlags, ...action.data } }));
    }

    const flagsResponse = yield call(Api.updateWebsiteFrontendFlags, websiteId, action.data);
    yield put(updateWebsiteFlagsSucceeded(flagsResponse));

    // Just to make sure the flags are in sync
    const websiteResponse = yield call(Api.getWebsite, websiteId);
    yield put(requestWebsiteSucceeded(websiteResponse));
  } catch (error) {
    if (performOptimisticUpdate) {
      // Return to old frontend flags if the request fails
      yield put(updateWebsite({ frontendFlags: currentFrontendFlags }));
    }

    yield put(updateWebsiteFlagsFailed(error));
  } finally {
    yield delay(3000);
    yield put(resetRequest('updateWebsiteFlags'));
  }
}

export function* agreement() {
  try {
    const websiteId = yield select(getInstanceId);

    const agreementRequestData = {
      privacyTermsAccepted: true,
      dpaAccepted: true,
      businessConditionAccepted: true,
    };

    yield call(Api.agreement, websiteId, agreementRequestData);

    yield put(agreementSucceeded());
  } catch (error) {
    yield put(agreementFailed(error));
  }
}

export function* getAgreement() {
  try {
    const websiteId = yield select(getInstanceId);
    const data = yield call(Api.getAgreement, websiteId);
    yield put(getAgreementSucceeded(data));
  } catch (error) {
    yield put(getAgreementFailed(error));
  }
}

export function* signAgreement(action) {
  try {
    const websiteId = yield select(getInstanceId);
    const website = yield select(getWebsiteSelector);

    const websiteInfoDetailsChanged =
      action.websiteInfo.firstName !== website.firstName ||
      action.websiteInfo.lastName !== website.lastName ||
      action.websiteInfo.companyName !== website.companyName;
    if (websiteInfoDetailsChanged) {
      yield put(updateSettings(Immutable.fromJS(action.websiteInfo)));
      const updateWebsiteAction = yield take([UPDATE_SETTINGS_SUCCEEDED, UPDATE_SETTINGS_FAILED]);
      if (updateWebsiteAction.type !== UPDATE_SETTINGS_SUCCEEDED) {
        yield put(signDPAFailed());
        return;
      }
    }

    yield call(Api.signAgreement, websiteId, { signatureImage: action.signatureImage });
    yield call(getAgreement);
    yield put(signDPASucceeded());
  } catch (error) {
    yield put(signDPAFailed(error));
  }
}

export function* getFeaturePermissions() {
  try {
    const websiteId = yield select(getInstanceId);
    if (!isValidWebsiteId(websiteId)) throw new Error('No active website');
    const response = yield call(Api.getFeaturePermissions, websiteId);
    yield put(getFeaturePermissionsSucceeded(response));
  } catch (error) {
    yield put(getFeaturePermissionsFailed(error));
  }
}

export function* getAnnouncements() {
  try {
    const locale = yield select(getLocale);
    const website = yield select(getWebsiteSelector);

    const ipCountryISO = website && website.ipCountryISO;
    const response = yield call(Api.getAnnouncements, locale || 'en', ipCountryISO, website.id);
    const data = response?.payload[locale] || response?.payload['en'];
    yield put(getAnnouncementsSucceeded({ data: announcementsMapper(data) }));
  } catch (error) {
    yield put(getAnnouncementsFailed(error));
  }
}

export function* getPrivacySettingsWatcher() {
  try {
    const websiteId = yield select(getInstanceId);
    const response = yield call(Api.getPrivacySettings, websiteId);
    yield put(getPrivacySettingsSucceeded({ data: response?.payload }));
  } catch (error) {
    yield put(getPrivacySettingsFailed(error));
  }
}
